<script setup>
/**
 * Adding a device by QR code, from a device that is signed in — the other half
 * of the sign-in screen's "Show a code" / "Scan a code"
 * (tenantdb's api/devices.go). The phone always scans.
 *
 *   show     a one-time sign-in link as a code, renewed while the sheet is
 *            open, for the phone being added to scan
 *   scan     the camera, for the code a device waiting to be added shows
 *   approve  that device, before it is signed in: the six digits to compare
 *            with its screen, and what kind of device it is
 *
 * Drawn inside Settings' Sync tab, and on its own (DeviceSheet) when a
 * `?pair=` link arrives. It says `done` when it has nothing more to do.
 *
 * `payload.join` is scanning from local mode: a sign-in code then switches
 * this phone to the cloud, carrying its ledger along as switching always does.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { state, toast } from '../../stores/state'
import { errorText, t } from '../../stores/i18n'
import * as cloud from '../../stores/cloud'
import { setPendingMove } from '../../db/move'
import { describeDevice, readCode } from '../../lib/device-codes'
import { qrSvg, startScanner } from '../../camera'
import { readSyncCode } from '../../stores/sync'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })
const emit = defineEmits(['done'])

const mode = ref(props.payload?.mode ?? 'show')
const error = ref('')
const busy = ref(false)

// ------------------------------------------------------------------ show
const svg = ref('')
const shown = ref('')
const expires = ref(0)
const now = ref(Date.now())
const secondsLeft = computed(() => Math.max(0, Math.ceil((expires.value - now.value) / 1000)))
let clock = null

async function renew() {
  try {
    const link = await cloud.deviceLink()
    // Naming the org: the new device opens this one, whichever kind it is.
    shown.value = state.cloud.org ? `${link.url}&org=${encodeURIComponent(state.cloud.org.id)}` : link.url
    svg.value = await qrSvg(shown.value)
    expires.value = Date.parse(link.expiresAt)
    error.value = ''
  } catch (err) {
    error.value = errorText(err)
  }
}

// ------------------------------------------------------------------ scan
const video = ref(null)
let stopCamera = null

async function scan() {
  error.value = ''
  try {
    stopCamera = await startScanner(video.value, onCode)
  } catch (err) {
    error.value = t('device.noCamera', { reason: err?.message ?? String(err) })
  }
}

async function onCode(text) {
  // An encrypted ledger's code (shown by a device that syncs) is sync's to follow:
  // boot signs in, stays local and offers the ledger — never the cloud.
  if (readSyncCode(text)?.kind === 'join') {
    stopCamera?.()
    location.assign(readSyncCode(text).url)
    return
  }
  const code = readCode(text, location.origin)
  if (!code) {
    error.value = t('device.notOurs')
    return
  }
  if (code.kind === 'login') {
    if (!props.payload?.join) {
      error.value = t('device.alreadyIn')
      return
    }
    stopCamera?.()
    busy.value = true
    try {
      await cloud.verifyLink(code.token)
      setPendingMove({ to: 'cloud', stage: 'copy' })
      cloud.setMode('cloud')
      location.reload()
    } catch (err) {
      error.value = errorText(err)
      busy.value = false
    }
    return
  }
  stopCamera?.()
  stopCamera = null
  target.value = { id: code.id, secret: code.secret }
  mode.value = 'approve'
  look()
}

// ------------------------------------------------------------------ approve
const target = ref(props.payload?.id ? { id: props.payload.id, secret: props.payload.secret } : null)
const asking = ref(null)
const digits = computed(() => (asking.value ? `${asking.value.code.slice(0, 3)} ${asking.value.code.slice(3)}` : ''))
const device = computed(() => {
  const { browser, os } = describeDevice(asking.value?.userAgent)
  return [browser, os].filter(Boolean).join(' · ') || t('device.unknown')
})

async function look() {
  error.value = ''
  try {
    asking.value = await cloud.lookPairing(target.value.id, target.value.secret)
  } catch (err) {
    error.value = errorText(err)
  }
}

async function approve() {
  busy.value = true
  try {
    await cloud.approvePairing(target.value.id, target.value.secret)
    toast(t('device.approved'), 'good')
    emit('done')
  } catch (err) {
    error.value = errorText(err)
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  if (mode.value === 'show') {
    renew()
    clock = setInterval(() => {
      now.value = Date.now()
      // A code is good for two minutes; a fresh one takes its place.
      if (expires.value && secondsLeft.value === 0) {
        expires.value = 0
        renew()
      }
    }, 1000)
  } else if (mode.value === 'scan') scan()
  else if (mode.value === 'approve' && target.value) look()
})
// The camera is let go of whichever way the sheet closes.
onBeforeUnmount(() => {
  clearInterval(clock)
  stopCamera?.()
})
</script>

<template>
  <div>
    <div v-if="mode === 'show'" class="grid justify-items-center gap-4 text-center" data-device="show">
      <p class="text-[13.5px] text-muted">{{ t('device.showSheetLead') }}</p>
      <!-- eslint-disable-next-line vue/no-v-html -- our own SVG, drawn from a URL -->
      <div class="w-56 max-w-full overflow-hidden rounded-2xl" data-qr :data-link="shown" v-html="svg" />
      <p v-if="expires" class="tnum text-[12.5px] text-faint">{{ t('device.renewsIn', { seconds: secondsLeft }) }}</p>
    </div>

    <div v-else-if="mode === 'scan'" class="grid gap-4" data-device="scan">
      <p class="text-[13.5px] text-muted">{{ t('device.scanSheetLead') }}</p>
      <div class="relative aspect-square w-full overflow-hidden rounded-2xl bg-black">
        <video ref="video" class="size-full object-cover" playsinline muted />
        <div class="pointer-events-none absolute inset-[18%] rounded-xl border-[3px] border-white/85" />
      </div>
    </div>

    <div v-else class="grid justify-items-center gap-3 text-center" data-device="approve">
      <p class="text-[15px] font-semibold">{{ t('device.approveTitle') }}</p>
      <template v-if="asking">
        <p class="text-[13px] text-muted">{{ t('device.approveLead') }}</p>
        <p class="tnum text-[30px] font-bold tracking-[0.12em]" data-digits>{{ digits }}</p>
        <p class="flex items-center gap-1.5 text-[13px] text-muted"><Icon name="phone" :size="14" />{{ device }}</p>
      </template>
    </div>

    <p v-if="error" class="mt-4 flex items-start gap-2 text-[13px] text-danger" role="alert">
      <Icon name="alert" :size="15" class="mt-0.5 shrink-0" /> {{ error }}
    </p>

    <div class="mt-5 flex justify-end gap-2">
      <button class="btn-soft" @click="emit('done')">{{ mode === 'approve' ? t('common.cancel') : t('common.done') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" v-if="mode === 'approve' && asking" class="btn-primary" :disabled="busy" @click="approve">
        <Icon name="check" :size="16" /> {{ t('device.approve') }}
        <Kbd combo="mod+enter" />
      </button>
    </div>
  </div>
</template>
